import React, {useState} from "react";
import {
    Box, Card, Button, TextField, FormLabel, Stack,
    Link, Grid, Typography, IconButton, InputAdornment,
    styled, useTheme, 
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close';
import * as CONSTANTS from '../../constants'

import {
    user_signup_event
} from '../../services/login_system'

import {Visibility, VisibilityOff} from '@mui/icons-material'
import { checkPasswordComplexity } from 'check-password-complexity';


/**
 * Colors for the password strength bar
 * each color will represent a different strength level
 */
const getColors = (theme) => [
    {
        color: theme.palette.error.main,
        value: 'tooWeak',
    },
    {
        color: theme.palette.warning.main,
        value: 'weak',
    },
    {
        color: theme.palette.success.light,
        value: 'medium',
    },
    {
        color: theme.palette.success.main,
        value: 'strong',
    },
];

export const getPasswordStrengthResult = (strength, theme, lang) => {

    // default options
    switch (strength) {
        case 'tooWeak':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.TOO_WEAK[lang],
                color: theme?.palette.error.main
            };
        case 'weak':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.WEAK[lang],
                color: theme?.palette.warning.main
            };
        case 'medium':
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.OKAY[lang],
                color: theme?.palette.success.light
            };
        default:
            return {
                label: CONSTANTS.SIGN_UP_FORM.PASSWORD_STRENGTH.STRONG[lang],
                color: theme?.palette.success.dark
            };
    }
}


const Bar = styled('div', {
    // Configure which props should be forwarded on DOM
    shouldForwardProp: (prop) =>
        prop !== 'color' && prop !== 'inactive' && prop !== 'inactiveColor' && prop !== 'barWidth',
})(({ theme, color, inactive, inactiveColor, barWidth }) => {
    const defaultInactiveColor = inactiveColor || theme.palette.grey[300];

    const style = {
        height: 6,
        borderRadius: 6,
        backgroundColor: inactive ? defaultInactiveColor : color,
    };

    if (barWidth) {
        style.width = barWidth;
    } else {
        style.flex = 1;
    }

    return style;
});

function Copyright() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            {"Copyright © "}
            <Link color="inherit" href="https://material-ui.com/">
                Your Website
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </Typography>
    );
}

const SignUpForm = ({lang, onClose, setLoginMode}) => {

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [usernameError, setUsernameError] = useState(false);
    const [usernameErrorMessage, setUsernameErrorMessage] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    let isValidSignUp = false;

    const handleShowPassword = () => setShowPassword(!showPassword);

    const switchToLoginMode = () => {
        setLoginMode('Login');
    }

    const [strengthOption, setStrengthOption] = useState(null);
    const theme = useTheme();

    const handlePasswordChange = (event) => {
        const { value } = event.target;
        const result = checkPasswordComplexity(value);
        setStrengthOption(result);
    };

    const handleSubmit = async (e) => {

        e.preventDefault();
        if (!isValidSignUp) return;
        
        const [status, result]  = await user_signup_event(
            email, password, username
        )

        // success login
        if (status === 200) {
            // const res_username = result.username;
            // const res_email = result.email;
            // updateAuth(res_username, res_email); // Close the dialog on submit
            // show user registered.
            onClose();
        } 
        // failed login
        else {
            switch (result.message) {
                case 'Email is registered.':
                    setEmailError(true);
                    setEmailErrorMessage(CONSTANTS.SIGN_UP_FORM.ERROR_MESSAGE.REGISTERED_EMAIL[lang])
                    break;
                case 'Username is registered.':
                    setUsernameError(true);
                    setUsernameErrorMessage(CONSTANTS.SIGN_UP_FORM.ERROR_MESSAGE.REGISTERED_USERNAME[lang])
                    break;
                default:
                    setPasswordError(true);
                    setPasswordErrorMessage(CONSTANTS.SIGN_UP_FORM.ERROR_MESSAGE.FAILED_TO_REGISTER_ACCOUNT[lang]);
                    break;
            }
        }
        
    };

    const validateInputs = () => {
        const user_name = document.getElementById('username')
        const user_email = document.getElementById('email');
        const user_password = document.getElementById('password') ;

        let isValid = true;

        if (!user_name.value || user_name.value.length < 2) {
            setUsernameError(true);
            setUsernameErrorMessage(CONSTANTS.SIGN_UP_FORM.ERROR_MESSAGE.USERNAME_LENGTH[lang]);
            isValid = false;
        } else {
            setUsernameError(false);
            setUsernameErrorMessage('');
        }

        if (!user_email.value || !/\S+@\S+\.\S+/.test(user_email.value)) {
            setEmailError(true);
            setEmailErrorMessage(CONSTANTS.LOGIN_FORM.ERROR_MESSAGE.ENTER_VALID_EMAIL[lang]);
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!user_password.value || (strengthOption.value == "weak" || strengthOption.value == "tooWeak" )) {
            setPasswordError(true);
            setPasswordErrorMessage(CONSTANTS.LOGIN_FORM.ERROR_MESSAGE.PASSWORD_STRENGTH[lang]);
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        setUsername(user_name.value);
        setEmail(user_email.value);
        setPassword(user_password.value);

        isValidSignUp = isValid;

        return isValid;
    };

    return (
        <Card
            sx={{
                width: '25vw',
                padding: '3vw',
                minWidth: "300px",
            }}
        >
            <div>
                <Typography
                    component="h1"
                    variant="h4"
                    fontWeight='bold'
                    sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                    mb={3}
                >
                    {CONSTANTS.APP_BAR.REGISTER[lang]}
                </Typography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 1,
                        top: 1,
                    }}
                    tabIndex={"6"}
                >
                    <CloseIcon/>
                </IconButton>
            </div>
            <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                        <FormLabel htmlFor="email">{CONSTANTS.SIGN_UP_FORM.USERNAME[lang]+"*"}</FormLabel>
                        <TextField
                            error={usernameError}
                            helperText={usernameErrorMessage}
                            name="username"
                            variant="outlined"
                            required
                            fullWidth
                            id="username"
                            autoFocus
                            slotProps={{htmlInput: {tabIndex: "0"}}}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormLabel htmlFor="email">{CONSTANTS.LOGIN_FORM.EMAIL[lang]+"*"}</FormLabel>
                        <TextField
                            error={emailError}
                            helperText={emailErrorMessage}
                            variant="outlined"
                            required
                            fullWidth
                            id="email"
                            name="email"
                            placeholder="your@email.com"
                            autoComplete="email"
                            slotProps={{htmlInput: {tabIndex: "1"}}}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormLabel htmlFor="email">{CONSTANTS.LOGIN_FORM.PASSWORD[lang]+"*"}</FormLabel>
                        <div>
                            <TextField
                                error={passwordError}
                                helperText={passwordErrorMessage}
                                name="password"
                                placeholder="••••••"
                                type={showPassword ? "text" : "password"}
                                id="password"
                                autoComplete="current-password"
                                required
                                fullWidth
                                variant="outlined"
                                color={passwordError ? 'error' : 'primary'}
                                onChange={handlePasswordChange}
                                slotProps={{htmlInput: {tabIndex: "2"}}}
                                InputProps={{ // <-- This is where the toggle button is added.
                                    endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => handleShowPassword()}
                                            tabIndex={"3"}
                                        >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                    )
                                }}
                            />
                            {strengthOption && (
                                <Box sx={{ mt: 0.8 }}>
                                <Stack direction="row" spacing={3} alignItems="center">
                                    {/*
                                    * each strength level (4) will have a different color
                                    * it will be displayed as a bar
                                    */}
                                    <Stack direction="row" spacing={1} flex={1}>
                                    {getColors(theme).map((option, index) => (
                                        <Bar
                                        color={option.color}
                                        // the bar color depends on the strength level
                                        inactive={index + 1 > strengthOption?.score}
                                        />
                                        ))}
                                    </Stack>
                                    {/* label to be displayed depending of the strength level */}
                                    <Typography
                                    variant="caption"
                                    sx={{ color: getPasswordStrengthResult(strengthOption?.value, theme, lang).color }}
                                    >
                                    {getPasswordStrengthResult(strengthOption?.value, theme, lang).label}
                                    </Typography>
                                </Stack>
                                </Box>
                            )}
                        </div>
                    </Grid>
                    {/* <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox value="allowExtraEmails" color="primary" />}
                        label="I want to receive inspiration, marketing promotions and updates via email."
                    />
                    </Grid> */}
                </Grid>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    onClick={() => validateInputs()}
                    tabIndex={"4"}
                >
                    {CONSTANTS.APP_BAR.REGISTER[lang]}
                </Button>

                <Typography sx={{ textAlign: 'center' }}>
                    {CONSTANTS.SIGN_UP_FORM.ALREADY_HAVE_ACCOUNT[lang]+' '}
                    <span>
                        <Link
                            variant="body2"
                            sx={{ alignSelf: 'center' }}
                            onClick={() => switchToLoginMode()}
                            tabIndex="5"
                        >
                            {CONSTANTS.APP_BAR.LOGIN[lang]}
                        </Link>
                    </span>
                </Typography>

            </Box>
        </Card>
     );
}

export default SignUpForm;
